import { Link } from 'react-router-dom';
import { Container } from '../components/ui/Container';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users } from 'lucide-react';

export function About() {
	const { t } = useTranslation();

	const timeline = [
		{
			year: '2013 – 2023',
			title: t('about.timeline.2013-2023.title'),
			desc: t('about.timeline.2013-2023.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1743090232/kiota_suit/uejl8wcke1cctgelkwgn.jpg',
			accent: 'bg-amber-500',
			textAccent: 'text-amber-600',
			bgAccent: 'bg-amber-50',
		},
		{
			year: '2023',
			title: t('about.timeline.2023.title'),
			desc: t('about.timeline.2023.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1743085450/kiota_suit/z4dxuxwbkqq7xvfwxxni.jpg',
			accent: 'bg-blue-500',
			textAccent: 'text-blue-600',
			bgAccent: 'bg-blue-50',
		},
		{
			year: '2024',
			title: t('about.timeline.2024-immersion.title'),
			desc: t('about.timeline.2024-immersion.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1743073969/kiota_suit/az4pccpaeiex2isq8smw.jpg',
			accent: 'bg-teal-500',
			textAccent: 'text-teal-600',
			bgAccent: 'bg-teal-50',
		},
		{
			year: '2024',
			title: t('about.timeline.2024-recognition.title'),
			desc: t('about.timeline.2024-recognition.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1742912581/kiota_suit/Aristide/acswb2x2rbxbxuc577tx.jpg',
			accent: 'bg-purple-500',
			textAccent: 'text-purple-600',
			bgAccent: 'bg-purple-50',
		},
		{
			year: '2024',
			title: t('about.timeline.2024-pilot.title'),
			desc: t('about.timeline.2024-pilot.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1742918110/kiota_suit/x7dshq0jwbibdjzx4yqr.jpg',
			accent: 'bg-green-500',
			textAccent: 'text-green-600',
			bgAccent: 'bg-green-50',
		},
		{
			year: '2025',
			title: t('about.timeline.2025.title'),
			desc: t('about.timeline.2025.desc'),
			img: 'https://res.cloudinary.com/daxvxdecv/image/upload/v1743086219/kiota_suit/hayxezsmo4agdq4ywnkh.jpg',
			accent: 'bg-primary',
			textAccent: 'text-primary',
			bgAccent: 'bg-primary/5',
		},
	];

	return (
		<div className="min-h-screen bg-white">

			{/* ── HERO ─────────────────────────────────────────────────────── */}
			<div
				className="relative pt-24 pb-20 sm:pt-32 sm:pb-28 overflow-hidden"
				style={{
					backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0.55), rgba(15,23,42,0.70)), url('https://res.cloudinary.com/daxvxdecv/image/upload/v1742930300/kiota_suit/wiyivnk9wgf6zvniyo0t.jpg')`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				{/* Warm tint overlay */}
				<div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 to-transparent pointer-events-none" />

				<Container className="relative z-10">
					<div className="max-w-3xl mx-auto text-center">
						<span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm">
							{t('about.subtitle')}
						</span>
						<h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
							{t('about.title')}
						</h1>
						<p className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto">
							{t('about.intro') || 'De Goma à la scène internationale, une équipe convaincue que les PME africaines méritent une infrastructure numérique à la hauteur de leur ambition.'}
						</p>
					</div>
				</Container>

				{/* Bottom wave */}
				<svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none">
					<path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="white" />
				</svg>
			</div>

			{/* ── TIMELINE ─────────────────────────────────────────────────── */}
			<Container className="py-20 sm:py-28">
				<div className="relative">

					{/* ── Vertical spine — desktop only ── */}
					<div
						className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-amber-200 via-primary/30 to-primary"
						aria-hidden="true"
					/>

					{/* ── Vertical spine — mobile (left-anchored) ── */}
					<div
						className="lg:hidden absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-amber-200 via-primary/30 to-primary"
						aria-hidden="true"
					/>

					<div className="space-y-20 sm:space-y-28">
						{timeline.map((step, index) => {
							const isEven = index % 2 === 0;
							return (
								<div key={step.year + step.title} className="relative">

									{/* ── Dot on the spine ── */}
									{/* Desktop: centered */}
									<div className={`hidden lg:flex absolute left-1/2 top-6 -translate-x-1/2 w-5 h-5 rounded-full border-4 border-white shadow-md z-10 ${step.accent}`} />
									{/* Mobile: left-anchored */}
									<div className={`lg:hidden absolute left-6 top-6 -translate-x-1/2 w-4 h-4 rounded-full border-4 border-white shadow-md z-10 ${step.accent}`} />

									{/* ── Desktop: alternating two-column ── */}
									<div className={`hidden lg:flex items-start gap-12 xl:gap-20 ${isEven ? 'flex-row' : 'flex-row-reverse'}`}>

										{/* Text side */}
										<div className={`flex-1 ${isEven ? 'text-right' : 'text-left'}`}>
											<span className={`inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 ${step.bgAccent} ${step.textAccent}`}>
												{step.year}
											</span>
											<h3 className="text-2xl font-bold text-gray-900 mb-4">
												{step.title}
											</h3>
											<p className="text-gray-600 leading-relaxed text-base">
												{step.desc}
											</p>
										</div>

										{/* Spacer for dot */}
										<div className="w-5 flex-shrink-0" />

										{/* Image side */}
										<div className="flex-1">
											<div className="overflow-hidden rounded-2xl shadow-lg aspect-[4/3]">
												<img
													src={step.img}
													alt={step.title}
													className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
												/>
											</div>
										</div>
									</div>

									{/* ── Mobile: all left-anchored ── */}
									<div className="lg:hidden pl-14">
										<span className={`inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3 ${step.bgAccent} ${step.textAccent}`}>
											{step.year}
										</span>
										<h3 className="text-xl font-bold text-gray-900 mb-3">
											{step.title}
										</h3>
										<div className="overflow-hidden rounded-xl shadow-md aspect-[4/3] mb-4">
											<img
												src={step.img}
												alt={step.title}
												className="w-full h-full object-cover"
											/>
										</div>
										<p className="text-gray-600 leading-relaxed text-base">
											{step.desc}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</Container>

			{/* ── CTA ──────────────────────────────────────────────────────── */}
			<div className="bg-gradient-to-br from-gray-900 to-gray-800 py-16 sm:py-20">
				<Container>
					<div className="max-w-2xl mx-auto text-center">
						<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-6">
							<Users className="w-6 h-6 text-white" />
						</div>
						<h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
							{t('about.ctaTitle') || 'Rencontrez l\'équipe derrière Wanzo'}
						</h2>
						<p className="text-white/60 mb-8 leading-relaxed">
							{t('about.ctaDesc') || 'Des entrepreneurs, des ingénieurs et des experts financiers réunis autour d\'une même conviction : le numérique peut transformer l\'économie africaine.'}
						</p>
						<Link
							to="/team"
							className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
						>
							{t('about.ctaButton') || 'Découvrir l\'équipe'}
							<ArrowRight className="w-4 h-4" />
						</Link>
					</div>
				</Container>
			</div>
		</div>
	);
}